import { RefObject, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { passwordErrorText, validatePassword, validatePasswordAgain } from '../utils/password'

export interface NewPasswordField {
  value: string
  onChange: (value: string) => void
  onBlur: () => void
  error: string | null
  inputRef: RefObject<HTMLInputElement>
}

export interface NewPassword {
  password: string
  passwordAgain: string
  // Props for the `PasswordField` components.
  passwordField: NewPasswordField
  passwordAgainField: NewPasswordField
  // Call on submit: shows every error, focuses the first invalid field and tells if the pair is valid.
  submit: () => boolean
  // Clear both fields and their errors.
  reset: () => void
}

/**
 * State for a "new password" + "re-enter password" pair validated in the browser.
 *
 * An error is shown for a field once the user has left it or tried to submit, and from then on
 * it follows what they type, so it disappears as soon as the password is acceptable.
 */
export function useNewPassword(): NewPassword {

  const { t } = useTranslation()
  const [password, setPassword] = useState('')
  const [passwordAgain, setPasswordAgain] = useState('')
  const [touched, setTouched] = useState({ password: false, passwordAgain: false })
  const passwordRef = useRef<HTMLInputElement>(null)
  const passwordAgainRef = useRef<HTMLInputElement>(null)

  const passwordError = validatePassword(password)
  const passwordAgainError = validatePasswordAgain(password, passwordAgain)

  const submit = () => {
    setTouched({ password: true, passwordAgain: true })
    if (passwordError) {
      passwordRef.current?.focus()
      return false
    }
    if (passwordAgainError) {
      passwordAgainRef.current?.focus()
      return false
    }
    return true
  }

  const reset = () => {
    setPassword('')
    setPasswordAgain('')
    setTouched({ password: false, passwordAgain: false })
  }

  return {
    password,
    passwordAgain,
    passwordField: {
      value: password,
      onChange: setPassword,
      onBlur: () => setTouched(old => ({ ...old, password: true })),
      error: touched.password ? passwordErrorText(t, passwordError) : null,
      inputRef: passwordRef,
    },
    passwordAgainField: {
      value: passwordAgain,
      onChange: setPasswordAgain,
      onBlur: () => setTouched(old => ({ ...old, passwordAgain: true })),
      error: touched.passwordAgain ? passwordErrorText(t, passwordAgainError) : null,
      inputRef: passwordAgainRef,
    },
    submit,
    reset,
  }
}
